import React, { useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import ProfilePhoto from '../../../../../components/ProfilePhoto'
import Text from '../../../../../components/Text'
import Button from '../../../../../components/Button'
import Row from '../../../../../components/Row'
import Column from '../../../../../components/Column'
import Checkbox from '../../../../../components/Checkbox'
import SvgFragment from '../../../../../fragments/SvgFragment'
import { useAuthStore } from '../../../../../stores/auth/useAuthStore'
import { styles } from '../../../../../styles/themes/style'
import { AppRoutes } from '../../../../../interfaces/store/appStore'

export type InboxNonConnectionEnrichFlags = {
    privateEmailChecked: boolean
    businessEmailChecked: boolean
    phoneChecked: boolean
}

const displayName = (p: any) =>
    [p?.firstName, p?.lastName].filter(Boolean).join(' ').trim() || p?.name || 'Unknown'

const initials = (p: any) => {
    const f = typeof p?.firstName === 'string' ? p.firstName.charAt(0) : ''
    const l = typeof p?.lastName === 'string' ? p.lastName.charAt(0) : ''
    const two = `${f}${l}`.trim()
    return two.length ? two.toUpperCase() : '—'
}

export interface IInboxNonConnectionSidebarProps {
    participant: any
    onAddToLeadDelta: () => void | Promise<void>
    addingToLeadDelta: boolean
    onAddToLeadDeltaAndEnrich: (flags: InboxNonConnectionEnrichFlags) => void | Promise<void>
    addAndEnrichInProgress: boolean
}

const CREDIT_USAGE_ROWS = [
    { value: 'Business email', credits: '(1 Credits)' },
    { value: 'Personal email', credits: '(3 Credits)' },
    { value: 'Phone number', credits: '(10 Credits)' },
] as const

const InboxNonConnectionSidebar: React.FC<IInboxNonConnectionSidebarProps> = ({
    participant,
    onAddToLeadDelta,
    addingToLeadDelta,
    onAddToLeadDeltaAndEnrich,
    addAndEnrichInProgress,
}) => {
    const navigate = useNavigate()
    const { store: { workspace } } = useAuthStore()
    const [enrichOpen, setEnrichOpen] = useState(true)
    const [privateEmailChecked, setPrivateEmailChecked] = useState(false)
    const [businessEmailChecked, setBusinessEmailChecked] = useState(false)
    const [phoneChecked, setPhoneChecked] = useState(false)

    const name = displayName(participant)
    const sub = participant?.headline || participant?.occupation || ''
    const canTryAdd = Boolean(
        participant?.publicIdentifier || participant?.memberId || participant?.profileId,
    )

    const anyChannelChecked = phoneChecked || businessEmailChecked || privateEmailChecked
    const creditsLeft = (workspace?.enrichmentCredits ?? 0) > 0
    const enrichBlocked = !canTryAdd || addingToLeadDelta || addAndEnrichInProgress
    const enrichPrimaryDisabled =
        enrichBlocked || !anyChannelChecked || !creditsLeft

    const enrichTooltip = !canTryAdd
        ? 'LinkedIn profile could not be resolved for import.'
        : !creditsLeft
            ? 'No enrichment credits left.'
            : !anyChannelChecked
                ? 'Select at least one enrichment type.'
                : undefined

    return (
        <Root>
            <HeaderPhoto
                source={participant?.profilePicture}
                capitalLetters={initials(participant)}
                allowTooltip={false}
                width="64px"
                height="64px"
                minWidth="64px"
                minHeight="64px"
            />
            <section>
                <Text $bold $black $heading4>{name}</Text>
                {sub ? <Text $black>{sub}</Text> : null}
            </section>
            <Column gap="16px">
                <EnrichCard>
                    <Button
                        $spaceBetween
                        $type="white"
                        onClickHandler={() => setEnrichOpen(!enrichOpen)}
                    >
                        <div>Enrich contact</div>
                        <Chevron $open={enrichOpen} xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 7">
                            <path
                                fillRule="evenodd"
                                d="M6.193 14.215l.623.6c.257.246.674.247.932 0L12 10.747l4.252 4.068c.258.247.675.247.932 0l.623-.6c.257-.247.257-.648 0-.896l-5.34-5.133c-.258-.248-.676-.248-.933 0l-5.34 5.133c-.258.247-.258.649 0 .896z"
                                transform="translate(-209 -182) translate(30 120) translate(11 54) translate(162)"
                            />
                        </Chevron>
                    </Button>
                    {enrichOpen ? (
                        <Column gap="8px" style={{ padding: '0 8px 8px' }}>
                            <Row gap="10px" alignItems style={{ height: 20 }}>
                                <Checkbox
                                    checked={businessEmailChecked}
                                    checkboxId="inbox-noconn-be"
                                    disabled={enrichBlocked}
                                    onClickHandler={() => setBusinessEmailChecked(v => !v)}
                                />
                                <Text $bold $lighter>Find business email</Text>
                            </Row>
                            <Row gap="10px" alignItems style={{ height: 20 }}>
                                <Checkbox
                                    checked={privateEmailChecked}
                                    checkboxId="inbox-noconn-pe"
                                    disabled={enrichBlocked}
                                    onClickHandler={() => setPrivateEmailChecked(v => !v)}
                                />
                                <Text $bold $lighter>Find personal email</Text>
                            </Row>
                            <Row gap="10px" alignItems style={{ height: 20 }}>
                                <Checkbox
                                    checked={phoneChecked}
                                    checkboxId="inbox-noconn-ph"
                                    disabled={enrichBlocked}
                                    onClickHandler={() => setPhoneChecked(v => !v)}
                                />
                                <Text $bold $lighter>Find phone number</Text>
                            </Row>
                            <Row gap="10px" style={{ marginTop: 8, marginBottom: 8 }}>
                                {creditsLeft ? (
                                    <Button
                                        $bigButton
                                        $type="blue"
                                        $customWidth
                                        $SVGtype="enrichDisabled"
                                        $customDisabled={styles.colors.primary400}
                                        disabled={enrichPrimaryDisabled}
                                        isLoading={addAndEnrichInProgress}
                                        loadingText="Enrich"
                                        tooltipMessage={enrichTooltip}
                                        onClickHandler={() =>
                                            void onAddToLeadDeltaAndEnrich({
                                                privateEmailChecked,
                                                businessEmailChecked,
                                                phoneChecked,
                                            })
                                        }
                                    >
                                        Enrich
                                    </Button>
                                ) : (
                                    <Button
                                        $bigButton
                                        $type="blue"
                                        $customWidth
                                        onClickHandler={() => navigate(AppRoutes.SETTINGS_BILLING)}
                                    >
                                        Buy credits
                                    </Button>
                                )}
                            </Row>
                            <EnrichmentCredits gap="5px" alignItems>
                                <SvgFragment type="coins" />
                                Credits left:{' '}
                                <span style={{ fontWeight: 'bold' }}>{workspace?.enrichmentCredits ?? 0}</span>
                                <InfoMenuSvg>
                                    <SvgFragment type="info" />
                                    <InfoMenu gap="8px">
                                        <Text $bold $lighter>Credit usage:</Text>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                            {CREDIT_USAGE_ROWS.map((item, key) => (
                                                <div key={key} style={{ display: 'flex', gap: '5px' }}>
                                                    <Text $bold>{item.value}</Text>
                                                    <Text>{item.credits}</Text>
                                                </div>
                                            ))}
                                        </div>
                                    </InfoMenu>
                                </InfoMenuSvg>
                            </EnrichmentCredits>
                        </Column>
                    ) : null}
                </EnrichCard>
                <ActionRow gap="10px">
                    <Button
                        $type="blue"
                        disabled={!canTryAdd || addingToLeadDelta || addAndEnrichInProgress}
                        tooltipMessage={
                            !canTryAdd
                                ? 'LinkedIn profile could not be resolved for import.'
                                : undefined
                        }
                        onClickHandler={() => void onAddToLeadDelta()}
                    >
                        {addingToLeadDelta ? 'Adding…' : 'Add to LeadDelta'}
                    </Button>
                </ActionRow>
            </Column>
        </Root>
    )
}

const Root = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
    padding: 0 0 8px;
`

/** Match conversation list: fixed box + circle; parent column uses align-items: stretch otherwise the trigger stretches wide. */
const HeaderPhoto = styled(ProfilePhoto).attrs({ className: 'inbox-conversation-avatar' })`
    align-self: flex-start;
    flex-shrink: 0;
    border-radius: 50%;
`

const ActionRow = styled(Row)`
    width: 100%;
    align-items: center;

    & > * {
        flex: 1;
        min-width: 0;
    }
`

const EnrichCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    background: ${styles.colors.white};
    border-radius: 4px;
`

const Chevron = styled.svg<{ $open: boolean }>`
    fill: ${({ $open, theme }) => ($open ? theme.quaternaryColor : styles.colors.black300)};
    transition: transform 0.2s ease;
    transform: rotate(180deg);
    ${({ $open }) => $open && 'transform: rotate(360deg);'}
`


const EnrichmentCredits = styled(Row)`
    background-color: ${styles.colors.white};
    padding: 5px;
    border-radius: 4px;
    font-size: 14px;
    line-height: 20px;
    color: ${styles.colors.black600};
    align-items: center;
`

const InfoMenu = styled(Column)`
    display: none;
    position: absolute;
    padding: 8px;
    width: 200px;
    z-index: 1;
    background-color: white;
    border-radius: 4px;
    right: -25px;
    box-shadow: 0 0 12px 0 rgba(0, 0, 0, 0.1);
`

const InfoMenuSvg = styled.div`
    position: relative;
    cursor: pointer;
    display: flex;
    align-items: center;

    &:hover ${InfoMenu} {
        display: flex;
    }
`

export default InboxNonConnectionSidebar
